import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Maximize2, Search, Shield, Users } from 'lucide-react';
import QuotaSpendCharts from '../components/QuotaSpendCharts';
import { GatewayDrawer, TablePager, fieldClass, ghostBtn, initials, pageSlice, tdClass, thClass, trClass } from '../components/GatewayUi';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaScope, QuotaUser } from '../governedMcpTypes';
import { departmentMatches } from '../quotaDirectory';
import { buildEffectiveQuotaUsers, clientLabel, formatQuotaMonth, formatTokens, resolveEffectiveQuota } from '../quotaUsage';

export default function QuotaScopeDrawer({
  open,
  scope,
  scopeId,
  name,
  clientId,
  month,
  isLight,
  onClose,
  onOpenUser,
}: {
  open: boolean;
  scope: QuotaScope;
  scopeId: string;
  name: string;
  clientId: string;
  month: string;
  isLight: boolean;
  onClose: () => void;
  onOpenUser: (user: QuotaUser) => void;
}) {
  const { state, quotaDirectory } = useGovernedMcp();
  const [maximized, setMaximized] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userPage, setUserPage] = useState(1);

  useEffect(() => {
    if (!open) {
      setMaximized(false);
      setUserQuery('');
      setUserPage(1);
    }
  }, [open]);
  const members = useMemo(() => {
    if (scope === 'role') return quotaDirectory.users.filter((user) => user.roleId === scopeId);
    if (scope === 'department') {
      const department = quotaDirectory.departments.find((item) => item.id === scopeId || departmentMatches(item, scopeId));
      return quotaDirectory.users.filter((user) =>
        department ? department.matchIds.includes(user.departmentId) || user.departmentId === department.id : user.departmentId === scopeId
      );
    }
    return [];
  }, [quotaDirectory, scope, scopeId]);

  const monthUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(state.apiKeys, state.quotaAssignments, state.quotaUsages, quotaDirectory, month),
    [month, quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );

  const scopedUsers = useMemo(() => {
    const memberIds = new Set(members.map((item) => item.id));
    return monthUsers.filter(
      (item) =>
        Boolean(item.userId && memberIds.has(item.userId)) &&
        (clientId === 'all' || item.clientId === clientId)
    );
  }, [clientId, members, monthUsers]);

  const clientName = clientId === 'all' ? 'All clients' : clientLabel(clientId, state.clients);
  const Icon = scope === 'department' ? Building2 : Shield;

  const userRows = useMemo(() => {
    return members.map((member) => {
      const usageRows = scopedUsers.filter((item) => item.userId === member.id);
      const fallbackClient = clientId === 'all' ? state.clients[0]?.id || '' : clientId;
      const resolved = resolveEffectiveQuota(
        state.quotaAssignments,
        member,
        usageRows[0]?.clientId || fallbackClient,
        month,
        quotaDirectory
      );
      const tokensUsed = usageRows.reduce((sum, item) => sum + item.tokensUsed, 0);
      const tokenLimit =
        clientId === 'all' && usageRows.length
          ? usageRows.reduce((sum, item) => sum + item.tokenLimit, 0)
          : resolved.tokenLimit;
      const row: QuotaUser = usageRows[0]
        ? { ...usageRows[0], tokenLimit, tokensUsed }
        : {
            id: `dir-${member.id}-${fallbackClient}`,
            clientId: fallbackClient,
            userId: member.id,
            name: member.name,
            email: member.email,
            role: member.roleName,
            roleId: member.roleId,
            departmentId: member.departmentId,
            source: resolved.source,
            tokenLimit,
            tokensUsed,
            dailyUsage: [],
            byModel: [],
            updatedAt: '',
          };
      return { member, row, tokensUsed, tokenLimit };
    });
  }, [clientId, members, month, quotaDirectory, scopedUsers, state.clients, state.quotaAssignments]);

  const used = userRows.reduce((sum, item) => sum + item.tokensUsed, 0);
  const limit = userRows.reduce((sum, item) => sum + item.tokenLimit, 0);
  const remaining = Math.max(0, limit - used);
  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return userRows;
    return userRows.filter(({ member }) => `${member.name} ${member.email}`.toLowerCase().includes(q));
  }, [userQuery, userRows]);
  const pagedUsers = pageSlice(filteredUsers, userPage);

  return (
    <GatewayDrawer
      open={open}
      title={name}
      subtitle={`${scope === 'department' ? 'Department' : 'Role'} · ${formatQuotaMonth(month)} · ${clientName}`}
      eyebrow="Governed MCP / Quotas & Spendings"
      icon={<Icon size={18} />}
      isLight={isLight}
      onClose={onClose}
      size="lg"
      maximizable
      maximized={maximized}
      onMaximizedChange={setMaximized}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Users', value: String(members.length) },
            { label: 'Effective limit', value: formatTokens(limit) },
            { label: 'Current spend', value: formatTokens(used) },
            { label: 'Remaining', value: formatTokens(remaining) },
          ].map((item) => (
            <div
              key={item.label}
              className={`rounded-xl border px-3.5 py-3 ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900'}`}
            >
              <p className={`text-[11px] uppercase tracking-wide ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
              <p className={`mt-1 text-sm font-semibold tabular-nums ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
            </div>
          ))}
        </div>
        <QuotaSpendCharts
          users={userRows.map((item) => item.row)}
          isLight={isLight}
          subtitle={`${formatQuotaMonth(month)} · ${clientName}`}
        />
        <div className={`rounded-xl border overflow-hidden ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'}`}>
          <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            <Users size={14} />
            <p className={`text-sm font-medium mr-auto ${isLight ? 'text-neutral-900' : 'text-white'}`}>Users</p>
            <div className="relative min-w-[200px] flex-1 max-w-xs">
              <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
              <input
                className={`${fieldClass(isLight)} pl-8 h-8 py-1`}
                placeholder="Filter users…"
                value={userQuery}
                onChange={(event) => {
                  setUserQuery(event.target.value);
                  setUserPage(1);
                }}
              />
            </div>
            <button
              type="button"
              className={`${ghostBtn(isLight)} h-8 px-2.5`}
              onClick={() => setMaximized(true)}
              aria-label="Maximize users"
            >
              <Maximize2 size={14} />
            </button>
          </div>
          {filteredUsers.length === 0 ? (
            <p className={`px-4 py-6 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No users in this {scope}.</p>
          ) : (
            <>
              <div className={`overflow-x-auto ${maximized ? 'max-h-[calc(100vh-22rem)]' : 'max-h-72'}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className={thClass()}> </th>
                      <th className={thClass()}>User</th>
                      <th className={thClass()}>Limit</th>
                      <th className={thClass()}>Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUsers.map(({ member, row, tokenLimit, tokensUsed }) => (
                      <tr key={member.id} className={trClass(isLight)}>
                        <td className={`${tdClass()} whitespace-nowrap`}>
                          <button type="button" className={`${ghostBtn(isLight)} h-8 px-2.5`} onClick={() => onOpenUser(row)}>
                            Details
                          </button>
                        </td>
                        <td className={tdClass()}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              className={`h-8 w-8 rounded-lg text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                                isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'
                              }`}
                            >
                              {initials(member.name)}
                            </span>
                            <div className="min-w-0">
                              <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{member.name}</p>
                              <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(tokenLimit)}</td>
                        <td className={`${tdClass()} tabular-nums`}>{formatTokens(tokensUsed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3">
                <TablePager page={userPage} total={filteredUsers.length} isLight={isLight} onPage={setUserPage} noun="user" />
              </div>
            </>
          )}
        </div>
      </div>
    </GatewayDrawer>
  );
}
